import React, { useCallback, memo } from 'react';
import { compact, map, get } from 'lodash/fp';
import { getImageUrlByTypeWithResizer, getImageUrlWithResizer } from 'utils/image';
import getPCRating from 'utils/get-pc-rating';
import PosterPlayIcon from 'components/Icons/item-play';
import { Metadata, Title } from 'components/ContentTitle';
import { DetailsObjectType } from 'store/pages/loaders/loader-types';
import {
  Cover,
  CoverWrapper,
  Header,
  StyledImageOverlay,
  SecondaryHeader,
} from './StyledComponents';

interface IDetailsHeader {
  data: DetailsObjectType;
  selectedSection: number;
  IsSectionCompletelyScrolled: boolean;
  onCoverFocus: () => void;
  onCoverEnter: (params: { id: string; type: string }) => void;
}

const coverOffsets = [0, 360];
const headerOffsets = [-300, 0];

const coverConfig = {
  leaveFor: {
    right: '',
    up: '@WATCH_CONTROLS',
    left: '@EXPAND_DESCRIPTION_BUTTON',
    down: '@EXTRA_CONTENT_SECTION',
  },
};

const COVER_ID = 'details-cover-element';

const formatDisplayRunTime = (seconds) => {
  if (!seconds) return '';

  const hours = Math.floor(seconds / 60 / 60);
  const minutes = Math.floor((seconds / 60) % 60);

  if (hours === 0) {
    return `${minutes}m`;
  }

  return `${hours}h ${minutes}m`;
};

const DetailsHeader: React.FC<IDetailsHeader> = (props) => {
  const { data, selectedSection, IsSectionCompletelyScrolled } = props;
  const { onCoverFocus, onCoverEnter } = props;

  const posterUrl = getImageUrlByTypeWithResizer(data, 'poster_blueprint', {
    width: 432,
    height: 624,
  });

  const title = (data || {}).name;
  const backgroundImage = getImageUrlByTypeWithResizer(data, 'landscape', {
    width: 1920,
    height: 600,
  });
  const runTime = formatDisplayRunTime(data.duration);
  const ageRating = get('age', getPCRating(data.mpaa));
  const genresToShow = map('name')(data.genres).slice(0, 2).join(', ');
  const metaDataInfoLine1 = compact([data.year, genresToShow, runTime, ageRating]).join(', ');

  const onEnter = useCallback(({ id }) => onCoverEnter({ id, type: 'cover' }), [onCoverEnter]);

  return (
    <>
      <Header imageUrl={backgroundImage}>
        {backgroundImage && <StyledImageOverlay />}
        <Title>{title}</Title>
        <Metadata>
          <div>{metaDataInfoLine1}</div>
        </Metadata>
        <CoverWrapper
          sectionName={'COVER'}
          id={'cover'}
          config={coverConfig}
          coverOffset={IsSectionCompletelyScrolled ? 180 : coverOffsets[selectedSection]}
          opacity={IsSectionCompletelyScrolled ? 0.5 : 1}
        >
          <Cover
            cover={posterUrl}
            onFocus={onCoverFocus}
            onEnter={onEnter}
            id={COVER_ID}
            payload={{ id: COVER_ID }}
          >
            <PosterPlayIcon />
          </Cover>
        </CoverWrapper>
      </Header>
      <SecondaryHeader
        isVisible={selectedSection > 0}
        headerOffset={headerOffsets[selectedSection]}
      >
        <Title>{title}</Title>
        <Metadata>{metaDataInfoLine1}</Metadata>
      </SecondaryHeader>
    </>
  );
};

export default memo(DetailsHeader);
